/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 *
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 *
 * Software credits:
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 *
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import moment from 'moment';
import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Popover, Row, Spin, Switch } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { getUserRole } from '@utils/authentication';
import { MESSAGES } from '@translations/labels';
import botSelect from '@svg/bot_select.svg';
import { useAgentId } from '@hooks/useAgentId';
import releasesActions from '@store/actions/releases';
import { useGetAgentStatus } from 'commons/hooks/useGetAgentStatus';
import { useTrainAgent } from 'commons/hooks/useTrainAgent';
import { useCorpusVersionSelection } from 'commons/hooks/useCorpusVersion';
import { ROUTES, AGENT_STATUS, ANALYZER_STATUS, IMPORT_STATUS, IMPORT_FILES_STATUS, AUTH } from '@constants';
import { AppContext } from 'commons/contexts/AppContext';
import { SocketContext } from 'commons/contexts/SocketContext';
import { sendLogout } from '@components/websocket/webSocketAPI';
import { usePageViews } from '@components/websocket/usePageViews';
import { ActiveSessions } from '@components/websocket/ActiveSessions';
import { isEmpty } from '@utils/validation/isEmpty';
import { errorAlert, successAlert } from '@store/reducers/alerts';
import { setIsUploading } from '@store/reducers/agents';
import getAXAPrivateConfig from '../../../api/axaPrivateConfig';
import { useIntentsSearch } from '../content/manage-the-bot/hooks/useIntentsSearch';
import { headerStyles, popover } from './headerStyles';
import MainMenu from './mainMenu/mainMenu';
import ConfirmAnalysisModal from './confirmAnalysisModal';
import { useAuthContext } from 'react-oauth2-code-pkce';

const labels = { ...MESSAGES.header, ...MESSAGES.commons };
const NOTIFICATION_EXPIRATION_TIME = 1;

const getTrainStatusInfo = status => {
  let trainStatusText = labels.trainStatusPending;
  let trainStatusClassName = 'accent-negative';
  if (status === AGENT_STATUS.Trained) {
    trainStatusText = labels.trainStatusOK;
    trainStatusClassName = 'accent-positive';
  } else if (status === AGENT_STATUS.Training) {
    trainStatusText = labels.trainingStatus;
    trainStatusClassName = 'accent-informative';
  }
  return { trainStatusText, trainStatusClassName };
};

const getAnalysisStatusInfo = analyzerStatus => {
  let analysisStatusText = labels.analysisStatusPending;
  let analysisStatusClassName = 'accent-negative';
  if ([ANALYZER_STATUS.Analyzed, ANALYZER_STATUS.Idle].includes(analyzerStatus)) {
    analysisStatusText = labels.analysisStatusOK;
    analysisStatusClassName = 'accent-positive';
  } else if (analyzerStatus === ANALYZER_STATUS.Analyzing) {
    analysisStatusText = labels.analyzingStatus;
    analysisStatusClassName = 'accent-informative';
  }
  return { analysisStatusText, analysisStatusClassName };
};

const getImportStatusInfo = imoportStatus => {
  let statusText = labels.importStatusKO;
  let statusClassName = 'accent-negative';
  if (imoportStatus === IMPORT_STATUS.Idle) {
    statusText = labels.importStatusOK;
    statusClassName = 'accent-positive';
  } else if (imoportStatus === IMPORT_STATUS.Importing) {
    statusText = labels.importIsStatusPending;
    statusClassName = 'accent-informative';
  }
  return { statusText, statusClassName };
};

const isNotificationExpired = createdAt =>
  Math.abs(Date.now() - new Date(createdAt)) / 36e5 > NOTIFICATION_EXPIRATION_TIME;

const Header = () => {
  const [currentEnvironment, setCurrentEnvironment] = useState(null);
  const [currentPkgVersion, setCurrentPkgVersion] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showAnalysisFinishedToast, setShowAnalysisFinishedToast] = useState(false);

  const { name } = useSelector(state => state.authentication.user);
  const { agentId } = useAgentId();
  const { trainAgent } = useTrainAgent();
  const { getSelectedVersion } = useCorpusVersionSelection();
  const { searchIntents } = useIntentsSearch();
  const updates = useSelector(state => state.agents.updates);

  const corpusVersion = getSelectedVersion();
  const currentVersionId = !!corpusVersion && corpusVersion.versionId;
  const currentVersionTitle = !!corpusVersion && corpusVersion.versionTitle;
  const intents = useSelector(state => state.intents.all);
  const analysisStatus = useSelector(state => state.agents?.updates?.analyzer?.status);
  const {
    isTrainInProgress,
    setIsTrainInProgress,
    disableDeployToLiveBot,
    setAxaConfig,
    axaConfig,
    isTrainPending,
    setIsTrainPending,
    isAnalysePending,
    setIsAnalysePending,
    authLoginMethod,
  } = useContext(AppContext);

  let logoutFn = undefined;

  if (authLoginMethod === AUTH.ONEACCOUNT_LOGIN) {
    const { logOut } = useAuthContext();
    logoutFn = logOut;
  }
  
  const dispatch = useDispatch();
  const { socket, activeSessions } = useContext(SocketContext);

  usePageViews(socket);

  const shouldShowButtonDisabled = () => {
    return analysisStatus === 'Analyzing' || isAnalysePending || isTrainPending || isTrainInProgress;
  };

  useEffect(() => {
    const firstCallAXAconfig = async () => {
      const axaPrivateConfig = await getAXAPrivateConfig();
      setAxaConfig(axaPrivateConfig);
      setCurrentEnvironment(axaPrivateConfig.environment);
      setCurrentPkgVersion(axaPrivateConfig.version);
    };
    firstCallAXAconfig();
  }, []);

  const { importFiles } = updates || {};
  useEffect(() => {
    if (!isEmpty(importFiles)) {
      const { importStatus = {}, lastImport } = importFiles;
      const { collectionsStatus } = importStatus;
      if (
        collectionsStatus.length &&
        collectionsStatus.every(file => file.status === IMPORT_FILES_STATUS.FINISHED || IMPORT_FILES_STATUS.ERROR)
      ) {
        dispatch(setIsUploading(false));
        if (lastImport && !isEmpty(lastImport)) {
          const { error, intended_files: intendedFiles, createdAt } = lastImport;
          if (isNotificationExpired(createdAt)) return;
          if (error)
            dispatch(errorAlert(`Last file upload failed, description: ${error}. Files: ${intendedFiles.join(', ')}`));
          else dispatch(successAlert(`Last file upload was completed succesfully. Files: ${intendedFiles.join(', ')}`));
        }
      }
    }
  }, [importFiles?.importStatus?.timeLastUpdate]);

  useEffect(() => {
    if (analysisStatus !== 'Analyzing' && !isAnalysePending && showAnalysisFinishedToast) {
      dispatch(successAlert('Analysis finished'));
      setShowAnalysisFinishedToast(false);
    }
  }, [analysisStatus, isAnalysePending]);

  const handleTrain = (currentVersionId, includeAnalysis) => {
    const unblockTrainAndAnalysisAfterGetInfoFromWebSocket = () =>
      setTimeout(() => {
        setIsTrainPending(false);
        setIsAnalysePending(false);
      }, 6000);

    //Getting analysis status from websocket takes a while, so we block buttons
    //for 6 seconds to make sure to got the info. (we mark train and analysis process as pending)
    setIsTrainPending(true);
    setIsTrainInProgress(true);
    if (includeAnalysis) {
      setIsAnalysePending(true);
      setShowAnalysisFinishedToast(true);
    }

    unblockTrainAndAnalysisAfterGetInfoFromWebSocket();

    trainAgent(agentId, currentVersionId, !!includeAnalysis);
  };

  const displayRole = () => {
    const role = getUserRole();
    return role ? `(${role.replace('foreign', 'disabled').toUpperCase()})` : '';
  };

  const handleLogOut = () => {
    sendLogout(socket);
    if (logoutFn) logoutFn();
  };

  const lastAgentStatusInfo = useGetAgentStatus(socket);
  let trainedAt;
  let trainStatusText;
  let trainStatusClassName;
  let analysisStatusText;
  let analysisStatusClassName;
  let importStatusText;
  let importStatusClassName;

  if (lastAgentStatusInfo) {
    const { updates } = lastAgentStatusInfo;
    if (updates) {
      if (updates.importCorpus) {
        const importInfo = getImportStatusInfo(updates.importCorpus.status);
        importStatusText = importInfo.statusText;
        importStatusClassName = importInfo.statusClassName;
        if (updates.importCorpus.isCorpusOutdated) {
          // TODO: ask for intents & corpus
          searchIntents(agentId);
        }
      }

      if (updates.deployment) {
        const deploymentUpdate = updates.deployment;
        // TODO: get from backend config after cfg refactor
        if (deploymentUpdate.status === 'Deployed') {
          dispatch(releasesActions.finishDeployRelease(agentId));
        } else if (deploymentUpdate.status === 'Deploy Failed') {
          dispatch(releasesActions.getReleases(agentId));
        }
      }

      let trainQuery = {};
      if (updates.training) {
        trainQuery = updates.training.query;
        trainedAt = updates.training.trainedAt;
        ({ trainStatusText, trainStatusClassName } = getTrainStatusInfo(updates.training.status));
        ({ analysisStatusText, analysisStatusClassName } = getAnalysisStatusInfo(updates.analyzer.status));

        if ([AGENT_STATUS.Training].includes(updates.training.status)) {
          setIsTrainInProgress(true);
        } else if ([AGENT_STATUS.Trained, AGENT_STATUS.TrainingFailed].includes(updates.training.status)) {
          setIsTrainInProgress(false);
        }
      }
    }
  }

  const contentHoverBtnTrain = (
    <div
      style={{
        fontSize: '14px',
        minWidth: '200px',
        padding: '8px 10px',
      }}
    >
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '17px',
        }}
      >
        Train {currentVersionTitle ? `'${currentVersionTitle}'` : "'Corpus'"}
      </div>
      <p>
        {`${labels.lastTraining}: `}
        {updates?.training?.versionTitle ? updates?.training?.versionTitle : 'Corpus'}
        <time dateTime={trainedAt}>{trainedAt ? ` (${moment(trainedAt).fromNow()})` : ''}</time>
      </p>
    </div>
  );

  const contentHoverBtnAnalyse = (
    <div
      style={{
        fontSize: '14px',
        minWidth: '200px',
        padding: '8px 10px',
      }}
    >
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '17px',
        }}
      >
        Train & Analyse {currentVersionTitle ? `'${currentVersionTitle}'` : "'Corpus'"}
      </div>
      <p>Check selected corpus quality: mismatching intents, bad classification, volumetry.</p>
    </div>
  );

  return (
    <header className="header-wrapper z-40" role="banner">
      <div className="header-content fixed">
        <div className="banner">
          <img src={botSelect} alt="AXA Bots selector example" />
          {disableDeployToLiveBot && (
            <b>
              {' '}
              Deploy in live bot... <Spin />
            </b>
          )}
          <div style={{ display: 'flex', alignItems: 'end' }}>
            [{currentEnvironment}, v{currentPkgVersion}]
            {axaConfig?.webSocketOn && <ActiveSessions session={activeSessions} />}
          </div>
          <nav className="user-navigation" aria-labelledby="user-nav-label" role="navigation">
            <h3 id="user-nav-label" className="sr-only">
              {labels.title}
            </h3>
            <ul className="user-nav-list">
              <li>
                <span>
                  {name} {displayRole()}
                </span>
              </li>
              <li>
                <Link onClick={() => handleLogOut()} to={ROUTES.login.path} className="link-secondary block">
                  {labels.logout}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        <div className="flex space-between align-middle">
          <MainMenu />
          {getUserRole() !== 'viewer' && (
            <div style={{ display: 'flex' }}>
              <Popover placement="bottomLeft" content={contentHoverBtnAnalyse} trigger="hover">
                <Button
                  type="secondary"
                  disabled={shouldShowButtonDisabled()}
                  className={'uppercase ' + (shouldShowButtonDisabled() ? '' : 'custom-button-primary')}
                  onClick={() => setShowAnalysisModal(true)}
                  style={{
                    height: '35px',
                    padding: '5px 16px',
                    marginRight: '15px',
                  }}
                >
                  {analysisStatus === 'Analyzing' ? (
                    <div>
                      ANALYSING... <Spin />
                    </div>
                  ) : (
                    <div>ANALYSE</div>
                  )}
                </Button>
              </Popover>
              <Popover placement="bottomLeft" content={contentHoverBtnTrain} trigger="hover">
                <Button
                  type="primary"
                  disabled={shouldShowButtonDisabled()}
                  className="uppercase"
                  onClick={() => handleTrain(currentVersionId, false)}
                  style={{
                    height: '35px',
                    padding: '5px 16px',
                  }}
                >
                  {isTrainInProgress && analysisStatus !== 'Analyzing' ? (
                    <div>
                      TRAINING... <Spin />
                    </div>
                  ) : (
                    <div>TRAIN</div>
                  )}
                </Button>
              </Popover>
            </div>
          )}
        </div>
      </div>
      <ConfirmAnalysisModal
        isVisible={showAnalysisModal}
        onOK={() => {
          handleTrain(currentVersionId, true);
          setShowAnalysisModal(false);
        }}
        onCancel={() => {
          setShowAnalysisModal(false);
        }}
      />
      <style jsx>{headerStyles}</style>
      <style jsx>{popover}</style>
    </header>
  );
};

export { Header };
